import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth-context';
import { getSponsorPacks } from '@/lib/coreApi';
import { creditValueCentsToCredits, creditValueCentsToUsd, formatCreditsLabel, formatUsd } from '@/utils/credits';
import { Section, TableScroller, useBearer } from './_admin-utils';

interface CreditPackItem {
  id: string;
  name: string;
  usd_price: number;
  credit_value_cents: number;
  active: boolean;
  created_at: string | null;
}

export default function CreditPacks() {
  const { profile } = useAuth();
  const bearer = useBearer();
  const [items, setItems] = useState<CreditPackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!bearer) {
      setItems([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const result = await getSponsorPacks();
        if (!cancelled) {
          setItems(Array.isArray(result?.data?.items) ? result.data.items as unknown as CreditPackItem[] : []);
        }
      } catch (err) {
        if (!cancelled) {
          setItems([]);
          setError(err instanceof Error ? err.message : 'Unable to load packs');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bearer]);

  const isAdmin = ['admin', 'superadmin'].includes(profile?.role || '');
  if (!isAdmin) {
    return <div className="max-w-3xl mx-auto px-4 py-10 text-sm text-cocoa/70">You do not have permission to view this page.</div>;
  }

  return (
    <Section title="Tower Credit Packs">
      <p className="text-sm text-cocoa/70">
        Packs are seeded via database migrations for v1. This read-only view lists active offerings available to sponsors.
      </p>
      {error ? <p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-600">{error}</p> : null}
      <TableScroller minWidth={640}>
        <table className="min-w-full divide-y divide-sand/60 text-left text-sm text-cocoa">
          <thead className="bg-shell/70 text-xs uppercase tracking-wide text-cocoa/70">
            <tr>
              <th className="px-3 py-2 font-semibold">Name</th>
              <th className="px-3 py-2 font-semibold">USD price</th>
              <th className="px-3 py-2 font-semibold">Credits</th>
              <th className="px-3 py-2 font-semibold">Status</th>
              <th className="px-3 py-2 font-semibold">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-sand/60">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-center text-sm text-cocoa/60">
                  Loading packs…
                </td>
              </tr>
            ) : items.length ? (
              items.map((item) => (
                <tr key={item.id} className="bg-white/90">
                  <td className="px-3 py-3 font-medium text-cocoa">{item.name}</td>
                  <td className="px-3 py-3 text-cocoa/80">
                    ${(Number(item.usd_price) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-3 py-3 text-cocoa/80">
                    {formatCreditsLabel(creditValueCentsToCredits(item.credit_value_cents || 0))}
                    <span className="ml-1 text-xs text-cocoa/60">
                      (≈ {formatUsd(creditValueCentsToUsd(item.credit_value_cents || 0))})
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${
                        item.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'
                      }`}
                    >
                      {item.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-cocoa/70">
                    {item.created_at ? new Date(item.created_at).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-center text-sm text-cocoa/60">
                  No credit packs configured yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </TableScroller>
    </Section>
  );
}
